"use client";

import { useState, useEffect } from "react";
import { getScoops } from "../../services/scoops";
import { X } from "lucide-react";

const FilterSidebar = ({
  onApplyFilters,
  onClear,
  onClose,
  isMobile = false
}) => {
  const [categories, setCategories] = useState([]);
  const [allHoops, setAllHoops] = useState([]);
  const [hoops, setHoops] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedHoops, setSelectedHoops] = useState([]);

  let result = {};
  // Fetch categories and hoops on component mount
  useEffect(() => {
    const fetchScoops = async () => {
      try {
        const scoopResponse = await getScoops();
        setCategories(scoopResponse || []);

        // Extract all unique hoops from all categories
        const allHoop = [];
        scoopResponse.forEach(scoop => {
          if (scoop.hoops && Array.isArray(scoop.hoops)) {
            result[scoop.scoopid] = [];
            scoop.hoops.forEach(hoop => {
              result[scoop.scoopid].push(hoop);
              if (!allHoop.some(h => h.hoopid === hoop.hoopid)) {
                allHoop.push(hoop);
              }
            });
          }
        });
        setAllHoops(result);
      } catch (error) {
        console.error("Error fetching scoops:", error);
      }
    };

    fetchScoops();
  }, []);

  useEffect(
    () => {
      const fetchHoops = async () => {
        try {
          const hoops = [];
          const seenIds = new Set();

          for (let category of selectedCategories) {
            const hoop = allHoops[category];
            for (const item of hoop) {
              if (!seenIds.has(item.hoopid)) {
                seenIds.add(item.hoopid);
                hoops.push(item);
              }
            }
          }

          setHoops(hoops);
        } catch (error) {
          console.error("Error fetching scoops:", error);
        }
      };

      fetchHoops();
    },
    [selectedCategories]
  );

  const toggleCategory = category => {
    setSelectedCategories(prev => {
      if (prev.includes(category.scoopid)) {
        return prev.filter(id => id !== category.scoopid);
      } else {
        return [...prev, category.scoopid];
      }
    });
  };

  const toggleHoop = hoop => {
    setSelectedHoops(prev => {
      if (prev.includes(hoop.hoopid)) {
        return prev.filter(id => id !== hoop.hoopid);
      } else {
        return [...prev, hoop.hoopid];
      }
    });
  };

  const clearAll = () => {
    setSelectedCategories([]);
    setSelectedHoops([]);
    onClear();
  };

  const handleApply = () => {
    onApplyFilters({
      categories: selectedCategories,
      hoops: selectedHoops
    });

    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay - Only shown when mobile and sidebar is open */}
      {isMobile && (
        <div
          className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300`}
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Container with transition */}
      <div
        className={`
          ${
            isMobile
              ? "fixed inset-y-0 left-0 z-50 w-[85%] max-w-sm"
              : "h-full w-full"
          }
          bg-white overflow-auto transition-transform duration-300 ease-in-out
          
        `}
      >
        {isMobile && (
          <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white z-10">
            <h2 className="text-xl font-semibold">Filters</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        <div className="p-6 font-afacad">
          <div className="mb-2">
            <h2 className="text-xl font-bold font-fraunces">
              <span>Filter By </span>
            </h2>
          </div>
          {/* Scoops Categories */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 font-fraunces">
              Scoops ({selectedCategories.length})
            </h3>
            <div className="flex flex-wrap gap-3">
              {categories.map(category => (
                <button
                  key={category.scoopid}
                  onClick={() => toggleCategory(category)}
                  className={`
                    rounded-full px-5 py-2 text-sm font-medium transition-all duration-200
                    ${
                      selectedCategories.includes(category.scoopid)
                        ? "bg-black text-white shadow-md transform scale-105"
                        : "bg-white text-gray-700 border border-gray-300 hover:border-gray-400"
                    }
                  `}
                >
                  {category.scoopname}
                </button>
              ))}
            </div>
          </div>

          {/* Hoops */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 font-fraunces">
              Hoops ({selectedHoops.length})
            </h3>
            <div className="flex flex-wrap gap-3">
              {hoops &&
                hoops.length > 0 &&
                hoops.map(hoop => (
                  <button
                    key={hoop.hoopid}
                    onClick={() => toggleHoop(hoop)}
                    className={`
                    rounded-full px-5 py-2 text-sm font-medium transition-all duration-200
                    ${
                      selectedHoops.includes(hoop.hoopid)
                        ? "bg-fuchsia-100 text-fuchsia-800 shadow-sm transform scale-105"
                        : "bg-white text-gray-700 border border-gray-300 hover:border-gray-400"
                    }
                  `}
                  >
                    {hoop.hooptag}
                  </button>
                ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-8 sticky bottom-0 bg-white pt-4 pb-2">
            <button
              onClick={clearAll}
              className="flex-1 py-3.5 text-center border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={handleApply}
              className="flex-1 py-3.5 text-center bg-purple-700 text-white rounded-lg font-medium hover:bg-purple-800 transition-colors shadow-md"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;
